import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { OidcCallbackComponent } from './auth/oidc-callback/oidc-callback.component';
import { ParentViewComponent } from './issues/parent-view/parent-view.component';
import { EduNavbarComponent } from './navbar/edu-navbar/edu-navbar.component';
import { ViewRegistrantsComponent } from './registration/view-registrants/view-registrants.component';
import { AuthGuard } from './shared/auth.guard';
import { PermissionGuard } from './shared/permissions/permission.guard';
import { HeadGuardGuard } from './shared/head-guard.guard';
import { OrganizationService } from './shared/organization.service';
import { ProfileCompleteGuard } from './shared/profile-complete.guard';
import { SelectedEnrollmentGuard } from './shared/selected-enrollment.guard';
import { TeamGuardGuard } from './shared/team-guard.guard';
import { StarWarsIntroComponent } from './translator/star-wars-intro/star-wars-intro.component';
import { MeetingJoinComponent } from './meeting-links/meeting-join/meeting-join.component';
import { registrationLinkGuard } from './registration/registration-link.guard';
import { DEV_ROUTES } from './dev-routes';
import { PERMISSIONS } from 'edu-core';
console.log('Environment Org:', environment.org);
const routes: Routes = [
  {
    path: '',
    loadChildren: environment.org === "CSTeam"
      ? () => import('./csteam-landing/csteam-landing.module').then(m => m.CsteamLandingModule)
      : environment.org === "ICTeam"
        ? () => import('./icteam-landing/landing.module').then(m => m.ICTeamLandingModule)
        : environment.org === "QLM"
          ? () => import('./qlm-landing/qlm-landing.module').then(m => m.QlmLandingModule)
          : () => import('./papyrus-landing/papyrus-landing.module').then(m => m.PapyrusLandingModule)
  },
  {
    // Landing route for the QLM OIDC redirect (Authorization Code + PKCE callback).
    path: 'auth/callback',
    component: OidcCallbackComponent,
  },
  {
    path: 'login',
    loadChildren: () => import('./login/login.module').then(m => m.LoginModule),
  },
  // CSTeam does not register students here: /register only asks for the school and
  // its country, then sends the student on to qlm.pro. No registration link is
  // involved, so registrationLinkGuard does not apply to that build.
  environment.org === "CSTeam"
    ? {
      path: 'register',
      loadChildren: () => import('./csteam-register/csteam-register.module').then(m => m.CsteamRegisterModule)
    }
    : {
      path: 'register',
      canActivate: [registrationLinkGuard],
      loadChildren: () => import('./registration/registration.module').then(m => m.RegistrationModule)
    },
  {
    path: 'help',
    loadChildren: () => import('./help/help.module').then(m => m.HelpModule)
  },
  {
    path: 'schools',
    loadChildren: () => import('./school-reports/school-reports.module').then(m => m.SchoolReportsModule)
  },
  {
    // Public: the link token in the url is the parent's only credential, so this
    // route sits outside /portal and carries no auth guard.
    path: 'fees',
    loadChildren: () => import('./fees/fees.module').then(m => m.FeesModule)
  },
  // Local-development-only routes (design sandboxes). Empty in every deployed
  // build via the `dev-routes.prod.ts` file replacement.
  ...DEV_ROUTES,
  {
    path: 'portal',
    component: EduNavbarComponent,
    canActivateChild: [AuthGuard, ProfileCompleteGuard, SelectedEnrollmentGuard],
    children: [
      {
        path: 'ai',
        loadChildren: () => import('./ai/ai.module-routing').then(m => m.AiRoutingModule)
      },
      {
        path: 'study',
        loadChildren: () => import('./issues/issues.module').then(m => m.IssuesModule),
      },
      {
        path: 'topics',
        loadChildren: () => import('./topics/topics.module').then(m => m.TopicsModule),
      },
      {
        path: 'home',
        loadChildren: () => import('./home/home.module').then(m => m.HomeModule)
      },
      {
        path: 'materials',
        loadChildren: () => import('./materials/materials.module').then(m => m.MaterialsModule)
      },
      {
        path: 'videos',
        loadChildren: () => import('./videos/videos.module').then(m => m.VideosModule)
      },
      {
        path: 'registrants',
        component: ViewRegistrantsComponent,
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'meetings',
        loadChildren: () => import('./meeting-links/meeting-links-routing.module').then(m => m.MeetingLinksRoutingModule),
        canActivate: [TeamGuardGuard],
        canActivateChild: [TeamGuardGuard]
      },
      {
        path: 'hackathon',
        loadChildren: () => import('./hackathon/hackathon.module').then(m => m.HackathonModule)
      },
      {
        path: 'submissions',
        loadChildren: () => import('./assignments/assignments-with-routing.module').then(m => m.AssignmentWithRoutingModule)
      },
      {
        path: 'students',
        loadChildren: () => import('./students/students.module').then(m => m.StudentsModule)
      },
      {
        path: 'calls',
        loadChildren: () => import('./calls/calls.module').then(m => m.CallsModule),
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'payments',
        loadChildren: () => import('./payments/payments.module').then(m => m.PaymentsModule),
        canActivate: [PermissionGuard],
        data: { permissions: [PERMISSIONS.payments.manage] }
      },
      {
        path: 'team',
        loadChildren: () => import('./team/team.module').then(m => m.TeamModule),
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'leaderboard',
        loadChildren: () => import('./points/points-with-routing.module').then(m => m.PointsWithRouting),
        canActivate: [() =>
          inject(OrganizationService).currentOrganizationId === environment.icteamOrganizationId
        ],
      },
      {
        path: 'milestones',
        loadChildren: () => import('./milestones/milestones.module').then(m => m.MilestonesModule),
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'lectures',
        loadChildren: () => import('./lectures/lectures.module').then(m => m.LecturesModule)
      },
      {
        path: 'questions',
        loadChildren: () => import('./posts/posts.module').then(m => m.PostsModule)
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
      },
      {
        path: 'support-system',
        loadChildren: () => import('./support-sessions-system/support-system.module').then(m => m.SupportSystemModule),
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'variants',
        loadChildren: () => import('./variants/variants.module').then(m => m.VariantsModule),
        canActivate: [PermissionGuard],
        data: { permissions: [PERMISSIONS.variants.manage] }
      },
      {
        path: 'announcements',
        loadChildren: () => import('./announcements/announcements.module').then(m => m.AnnouncementsModule)
      },
      {
        path: 'plans',
        loadChildren: () => import('./plans/plans.module').then(m => m.PlansModule),
        canActivate: [HeadGuardGuard]
      },
      {
        path: 'session-management',
        loadChildren: () => import('./session-management/session-management.module').then(m => m.SessionManagementModule),
        canActivate: [PermissionGuard],
        data: { permissions: [PERMISSIONS.sessions.manage] }
      },
      {
        path: 'timings',
        loadChildren: () => import('./timings/timings.module').then(m => m.TimingsModule),
        canActivate: [PermissionGuard],
        data: { permissions: [PERMISSIONS.timings.manage] }
      },
      {
        path: 'trophies',
        loadChildren: () => import('./trophies/trophies.module').then(m => m.TrophiesModule),
      },
      {
        path: 'team-excuses',
        loadChildren: () => import('./team-excuses/team-excuses.module').then(m => m.TeamExcusesModule)
      },
      {
        path: 'incidents',
        loadChildren: () => import('./incidents/incidents.module').then(m => m.IncidentsModule)
      },
      {
        path: 'whatsapp',
        loadChildren: () => import('./whatsapp/whatsapp.module').then(m => m.WhatsappModule),
        canActivate: [TeamGuardGuard]
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule),
        canActivate: [PermissionGuard],
        data: { permissions: [PERMISSIONS.organizationSettings.manage] }
      },
      {
        path: 'access-control',
        loadChildren: () => import('./settings/access-control/access-control.module').then(m => m.AccessControlModule)
      },
      { path: 'meetings/join/:id', component: MeetingJoinComponent },
      
      {
        path: 'admin',
        loadChildren: () => import('./admin/admin-routing.module').then(m => m.AdminDashboardRoutingModule)
      },
    ]
  },
  {
    path: 'starwars',
    component: StarWarsIntroComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'performance',
    loadChildren: () => import('./performance/performance.module').then(m => m.PerformanceModule)
  },
  {
    path: 'plan/parents',
    component: ParentViewComponent
  },
  {
    path: 'complete-profile',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./features/complete-profile/complete-profile.module').then(
        (m) => m.CompleteProfileModule,
      ),
  },
  {
    path: 'onboard',
    loadChildren: () =>
      import('./features/onboarding/onboarding.module').then(
        (m) => m.OnboardingFlowModule,
      ),
  },
  {
    path: 'teacher-onboarding',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./teacher-onboarding/teacher-onboarding.module').then(
        (m) => m.TeacherOnboardingModule,
      ),
  },
  {
    path: '**',
    redirectTo: 'login',
  }
];
@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
